import { FileUploadSharp } from '@mui/icons-material'
import { Autocomplete, Avatar, Box, Button, TextField, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { fileServices } from '../../../services/fileServices'
import { MuiColorInput } from 'mui-color-input'
import { getAllISOCodes } from 'iso-country-currency'
import { authService } from '../../../services/authService'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { defaultTheme } from '../../../theme/defaultThemes'

const OnboardCompany = ({
  userDetails,
  companyDetails,
  setCompanyDetails,
  isLoading,
  setLoading,
}) => {
  const navigate = useNavigate()
  const countryList = getAllISOCodes()
  const [error, setError] = useState('')

  useEffect(() => {
    setError('')
  }, [companyDetails])

  const createCompany = async (e) => {
    e.preventDefault()

    if (
      !companyDetails.company_name.trim() ||
      !companyDetails.default_currency_code ||
      !companyDetails.country_code
    ) {
      setError('Company name, country and default currency are required fields to create company!')
      return
    }

    setLoading(true)
    const res = await authService.createCompany({
      ...companyDetails,
      user_id: userDetails?.id,
      user: userDetails,
    })
    if (res.status) {
      // login and go to task page
      navigate('/login')
    } else {
      toast.error('Something went wrong!')
    }
  }

  const handleLogoUpload = async (e) => {
    if (userDetails) {
      const file = e.target.files[0]
      const result = (await fileServices.uploadFile(userDetails, file, 'company_logo')).data
      const key = result?.filePath?.split('/')[1] || ''

      const base64 = await fileServices.getBase64(userDetails, key, 'company_logo')

      setCompanyDetails({
        ...companyDetails,
        metadata: { ...companyDetails?.metadata, logo: key, logoBase64: base64 },
      })
    }
  }

  return (
    <Box
      component="form"
      className="onboardingForm"
      onSubmit={createCompany}
      style={{
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        background: ' #fff',
        borderRadius: 12,
        boxShadow: '0 24px 64px #26214a1a',
        padding: '60px 60px',
      }}
    >
      <Typography
        variant="h5"
        color="textSecondary"
        style={{
          color: '#27104E',
          fontSize: '21px',
          marginBottom: '10px',
          fontWeight: '800',
        }}
      >
        Welcome to Epoch7{' '}
      </Typography>
      <Typography
        style={{ marginBottom: '30px', color: '#4E62BB', fontSize: '12px', textAlign: 'center' }}
      >
        Setup your company to start things up
      </Typography>

      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Avatar
          alt={companyDetails?.company_name}
          src={`data:image/png;base64,${companyDetails?.metadata?.logoBase64}`}
          sx={{ width: 64, height: 64 }}
        />
      </Box>

      <TextField
        style={{ borderColor: '#D6D9DE', borderRadius: '10px' }}
        onChange={(e) => setCompanyDetails((state) => ({ ...state, company_name: e.target.value }))}
        label="Company Name"
        variant="outlined"
        color="primary"
        autoFocus
        fullWidth
        value={companyDetails.company_name}
        size="small"
        className="inputsout"
      />

      <Button
        variant="outlined"
        color="primary"
        size="small"
        component="label"
        fullWidth
        sx={{
          textAlign: 'left',
          color: 'primary',
          fontWeight: 400,
          border: '1px solid border.secondary',
          borderRadius: 2,
        }}
      >
        <span
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            height: '32px',
          }}
        >
          <span
            style={{ width: '100%', textAlign: 'left', marginLeft: 4, textTransform: 'capitalize' }}
            sx={{ color: 'text.secondary' }}
          >
            {companyDetails?.metadata?.logo ? 'Change Logo' : 'Upload Logo'}
          </span>
          <span style={{ textAlign: 'left', marginLeft: 4 }}>
            <FileUploadSharp sx={{ color: 'primary' }} />
          </span>
        </span>
        <input hidden type="file" onChange={handleLogoUpload} />
      </Button>

      {companyDetails?.metadata?.logo && (
        <Typography
          style={{
            width: '100%',
            color: '#4E62BB',
            fontSize: '12px',
            textAlign: 'left',
            marginTop: 4,
          }}
        >
          {companyDetails?.metadata?.logo?.split('-')[1]}
        </Typography>
      )}

      <TextField
        className="inputsout"
        onChange={(e) =>
          setCompanyDetails((state) => ({
            ...state,
            metadata: { ...state?.metadata, description: e.target.value },
          }))
        }
        label={'Enter Description'}
        type="text"
        variant="outlined"
        color="primary"
        fullWidth
        value={companyDetails?.metadata?.description}
        size="small"
        multiline
        maxRows={4}
        minRows={2}
        sx={{
          marginTop: 3,
          '& .MuiInputBase-root': {
            padding: '0px',
            lineHeight: '20px',
          },
        }}
      />

      <Box style={{ display: 'flex', alignItems: 'center', gap: 16, width: '100%' }}>
        <Autocomplete
          options={countryList.sort((a, b) => a?.countryName?.localeCompare(b?.countryName))}
          getOptionLabel={(it) => it.countryName}
          variant="outlined"
          color="primary"
          fullWidth
          value={countryList.find((it) => it.iso == companyDetails.country_code)}
          size="small"
          className="inputsout"
          onChange={(_, v) =>
            setCompanyDetails((state) => ({
              ...state,
              country_code: v.iso,
              default_currency_code: v.currency,
            }))
          }
          sx={{
            '& .MuiAutocomplete-endAdornment': {
              marginTop: '-6px',
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Country"
              sx={{
                '& .MuiInputBase-root': {
                  height: '40px',
                  lineHeight: '40px',
                  paddingRight: '30px',
                  padding: '0px',
                },
              }}
            />
          )}
        />
        <Autocomplete
          options={countryList.sort((a, b) => a?.currency?.localeCompare(b?.currency))}
          getOptionLabel={(it) => it.currency}
          variant="outlined"
          color="primary"
          fullWidth
          value={
            countryList.find((it) => it.currency == companyDetails.default_currency_code) || null
          }
          size="small"
          className="inputsout"
          onChange={(_, v) =>
            setCompanyDetails((state) => ({ ...state, default_currency_code: v.currency }))
          }
          sx={{
            '& .MuiAutocomplete-endAdornment': {
              marginTop: '-6px',
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Default Currency"
              sx={{
                '& .MuiInputBase-root': {
                  height: '40px',
                  lineHeight: '40px',
                  paddingRight: '30px',
                  padding: '0px',
                },
              }}
            />
          )}
        />
      </Box>

      <Box style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <MuiColorInput
          className="inputsout"
          variant="outlined"
          color="primary"
          fullWidth
          format="hex"
          label="Primary Color"
          value={companyDetails?.metadata?.primary_color || defaultTheme?.palette?.primary?.main}
          onChange={(v) =>
            setCompanyDetails((state) => ({
              ...state,
              metadata: { ...state?.metadata, primary_color: v },
            }))
          }
        />

        <MuiColorInput
          className="inputsout"
          variant="outlined"
          color="primary"
          fullWidth
          format="hex"
          label="Secondary Color"
          size="small"
          value={
            companyDetails?.metadata?.secondary_color || defaultTheme?.palette?.secondary?.main
          }
          onChange={(v) =>
            setCompanyDetails((state) => ({
              ...state,
              metadata: { ...state?.metadata, secondary_color: v },
            }))
          }
        />
      </Box>

      {error && (
        <Typography
          variant="caption"
          color="text.error"
          textAlign="center"
          style={{ marginBottom: 12 }}
        >
          {error}
        </Typography>
      )}

      <Button
        fullWidth
        disabled={isLoading}
        color="primary"
        variant="contained"
        className="onboarding-btn"
        onClick={createCompany}
        style={{ borderRadius: '9px' }}
        type="submit"
      >
        Create Account
      </Button>
    </Box>
  )
}

export default OnboardCompany
